import React from 'react';
import SeoWrapper from '../components/SeoWrapper';
import content from '../data/content.json';

const ServicesPage = ({ language }) => {
  const t = content.services;
  const isPl = language === 'pl';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": isPl ? "Usługi Informatyczne i Programistyczne" : "IT and Software Services",
    "description": isPl ? t.intro_pl : t.intro_en,
    "provider": {
      "@type": "Person",
      "name": "Kamil Priefer",
      "url": "https://priefer.pl"
    },
    "areaServed": {
      "@type": "City",
      "name": "Poznań"
    },
    "url": "https://priefer.pl/uslugi"
  };

  const processSteps = [
    {
      step: '1',
      title: isPl ? "Analiza" : "Analysis",
      desc: isPl ? "Rozpoznanie Twoich potrzeb i celów biznesowych." : "Understanding your needs and business goals."
    },
    {
      step: '2',
      title: isPl ? "Projekt" : "Design",
      desc: isPl ? "Dobór technologii i planowanie architektury." : "Selecting technology and planning architecture."
    },
    {
      step: '3',
      title: isPl ? "Wdrożenie" : "Implementation",
      desc: isPl ? "Kodowanie, testowanie i uruchomienie rozwiązania." : "Coding, testing, and launching the solution."
    },
    {
      step: '4',
      title: isPl ? "Wsparcie" : "Support",
      desc: isPl ? "Ciągła opieka techniczna i rozwój systemu." : "Continuous technical support and system development."
    }
  ];

  const ServiceSection = ({ title, description, items, colorClass }) => (
    <div className="mb-20">
      <div className="mb-10 text-center md:text-left">
        <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${colorClass === 'blue' ? 'text-[#04c7e1]' : 'text-[#f2852d]'}`}>
          {title}
        </h2>
        <p className="text-gray-300 max-w-2xl text-lg">
          {description}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((item, index) => (
          <div 
            key={index} 
            className={`glass p-8 rounded-xl border border-white/5 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg group ${
              colorClass === 'blue' ? 'hover:border-[#04c7e1]/30' : 'hover:border-[#f2852d]/30'
            }`}
          >
            <h3 className={`text-xl font-bold mb-3 group-hover:text-white transition-colors ${
              colorClass === 'blue' ? 'text-[#04c7e1]/80' : 'text-[#f2852d]/80'
            }`}>
              {item.title}
            </h3>
            <p className="text-gray-400 leading-relaxed text-sm">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <SeoWrapper
        title={isPl ? "Usługi" : "Services"}
        description={isPl ? t.intro_pl : t.intro_en}
        url="/uslugi"
        schema={schema}
        keywords="Usługi IT Poznań, Software House Poznań, Tworzenie Aplikacji, Helpdesk IT, Administracja Sieciami, Programista React, Outsourcing IT, Obsługa Informatyczna Firm"
      />
      <div className="min-h-screen pt-24 pb-12">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#04c7e1] to-[#f2852d]">
              {isPl ? "Oferta Współpracy" : "Service Offer"}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {isPl ? t.intro_pl : t.intro_en}
            </p>
          </div>

          {/* Services Sections */}
          <ServiceSection 
            title={isPl ? t.software_development.title_pl : t.software_development.title_en}
            description={isPl ? t.software_development.description_pl : t.software_development.description_en}
            items={isPl ? t.software_development.items_pl : t.software_development.items_en}
            colorClass="blue"
          />

          <ServiceSection 
            title={isPl ? t.it_support.title_pl : t.it_support.title_en}
            description={isPl ? t.it_support.description_pl : t.it_support.description_en}
            items={isPl ? t.it_support.items_pl : t.it_support.items_en}
            colorClass="green"
          />

          {/* Process Section */}
          <div className="mt-24 mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gradient">
              {isPl ? "Mój Proces" : "My Process"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative text-center group">
                  <div className="w-16 h-16 mx-auto bg-gray-800 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-6 border-2 border-gray-700 group-hover:border-[#04c7e1] group-hover:bg-[#04c7e1] transition-all duration-300 shadow-lg shadow-black/50">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>
                  <p className="text-gray-400 text-sm px-4">{step.desc}</p>
                  
                  {/* Connector Line (Desktop) */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-gray-800 -z-10 transform translate-x-8"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServicesPage;
